import React from "react";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function formatNumber(value, locale = "da-DK") {
  return new Intl.NumberFormat(locale).format(value);
}

function computeYTicks(maxValue) {
  if (!maxValue || maxValue <= 0) return [0, 1];
  const niceSteps = [
    1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000,
  ];
  const step =
    niceSteps.find((s) => maxValue / s <= 4) || Math.ceil(maxValue / 4);
  const ticks = [];
  for (let v = 0; v <= maxValue + step; v += step) {
    ticks.push(v);
    if (ticks.length > 6) break;
  }
  return ticks;
}

function formatYLabel(value, locale) {
  if (value >= 10000) {
    return new Intl.NumberFormat(locale, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  if (value >= 1000) {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
      value,
    );
  }
  return String(value);
}

export default function GraphCard({
  title,
  subtitle,
  unit,
  values,
  isWeekendValues,
  baseline,
  average,
  baselineLabel,
  averageLabel,
  weekendLabel,
  infoTitle,
  baselineInfoText,
  averageBaselineInfoText,
  averageInfoText,
  toggleInfoText,
  xLabels,
  xTooltipLabels,
  locale,
  showWeekendLegend,
}) {
  const safeValues = values.length > 0 ? values : [0];
  const safeWeekendValues = Array.isArray(isWeekendValues)
    ? isWeekendValues
    : [];
  const yTicks = computeYTicks(Math.max(...safeValues, baseline, average, 0));
  const niceMax = yTicks[yTicks.length - 1] || 1;
  const showEvery = Math.ceil(xLabels.length / 12) || 1;
  const barGap = safeValues.length > 20 ? "1px" : "2px";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        borderRadius: 3,
        background: "#f4f9fb",
        border: "1px solid rgba(126, 198, 221, 0.35)",
        color: "#0d3342",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{ color: "#0d3342", fontWeight: 600 }}
          >
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#123f52" }}>
            {subtitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.7} alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0d3342" }}>
            {unit}
          </Typography>
          <Tooltip
            arrow
            placement="left-start"
            enterDelay={120}
            title={
              <Box sx={{ maxWidth: 280, py: 0.4 }}>
                <Typography
                  sx={{ fontSize: "0.80rem", fontWeight: 700, mb: 0.6 }}
                >
                  {infoTitle}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.74rem",
                    fontWeight: 700,
                    lineHeight: 1.35,
                    mb: 0.5,
                  }}
                >
                  {baselineInfoText.title}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.70rem", lineHeight: 1.35, mb: 1 }}
                >
                  {baselineInfoText.description}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.74rem",
                    fontWeight: 700,
                    lineHeight: 1.35,
                    mb: 0.5,
                  }}
                >
                  {averageBaselineInfoText.title}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", lineHeight: 1.35, mb: 1 }}
                >
                  {averageBaselineInfoText.description}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.74rem",
                    fontWeight: 700,
                    lineHeight: 1.35,
                    mb: 0.5,
                  }}
                >
                  {averageInfoText.title}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.70rem", lineHeight: 1.35, mb: 1 }}
                >
                  {averageInfoText.description}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.74rem",
                    fontWeight: 700,
                    lineHeight: 1.35,
                    mb: 0.5,
                  }}
                >
                  {toggleInfoText.title}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.70rem", lineHeight: 1.35, mb: 1 }}
                >
                  {toggleInfoText.description}
                </Typography>
              </Box>
            }
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 18,
                color: "#4b7282",
                cursor: "help",
                "&:hover": { color: "#0d3342" },
              }}
            />
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          pt: 1,
          pb: 0.5,
          flex: 1,
          minHeight: 220,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 40,
            height: "100%",
            flexShrink: 0,
          }}
        >
          {yTicks.map((tick) => (
            <Box
              key={tick}
              sx={{
                position: "absolute",
                right: 4,
                top: `${((niceMax - tick) / niceMax) * 100}%`,
                transform: "translateY(-50%)",
                lineHeight: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.58rem",
                  color: "#5f7a86",
                  whiteSpace: "nowrap",
                }}
              >
                {formatYLabel(tick, locale)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              position: "relative",
              flex: 1,
              minHeight: 0,
              borderLeft: "1px solid #cde0e8",
              borderBottom: "1px solid #cde0e8",
            }}
          >
            {yTicks
              .filter((t) => t > 0)
              .map((tick) => (
                <Box
                  key={tick}
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: `${(tick / niceMax) * 100}%`,
                    borderTop: "1px dashed rgba(150,200,216,0.4)",
                  }}
                />
              ))}
            {baseline > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: `${Math.min(100, (baseline / niceMax) * 100)}%`,
                  borderTop: "2px dashed #f83d3d",
                  zIndex: 2,
                }}
              />
            )}
            {average > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: `${Math.min(100, (average / niceMax) * 100)}%`,
                  borderTop: "2px dashed #5ed0fa",
                  zIndex: 2,
                }}
              />
            )}
            <Stack
              direction="row"
              alignItems="flex-end"
              sx={{
                height: "100%",
                gap: barGap,
                px: "2px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {safeValues.map((value, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: 1,
                    height: "100%",
                    display: "flex",
                    alignItems: "flex-end",
                    position: "relative",
                    backgroundColor:
                      safeWeekendValues[index] === true
                        ? "rgba(95, 122, 134, 0.16)"
                        : "transparent",
                  }}
                >
                  <Tooltip
                    title={`${xTooltipLabels?.[index] || xLabels[index] || ""}: ${formatNumber(value, locale)} ${unit}`}
                    placement="top"
                    arrow
                    enterDelay={50}
                    enterNextDelay={0}
                    leaveDelay={0}
                    enterTouchDelay={0}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "offset",
                            options: { offset: [0, -4] },
                          },
                        ],
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: `${(value / niceMax) * 100}%`,
                        minHeight: value > 0 ? 8 : 0,
                        borderRadius: "3px 3px 0 0",
                        background: index % 2 ? "#34b6e6" : "#0e8cc0",
                        border: "1px solid transparent",
                        transition:
                          "border-color 120ms ease, filter 120ms ease",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: "#08384d",
                          filter: "brightness(0.95)",
                        },
                      }}
                    />
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          </Box>

          <Stack direction="row" sx={{ mt: 0.5 }}>
            {xLabels.map((label, index) => (
              <Box
                key={index}
                sx={{ flex: 1, textAlign: "center", overflow: "hidden" }}
              >
                {index % showEvery === 0 && (
                  <Tooltip
                    title={xTooltipLabels?.[index] || label || ""}
                    placement="top"
                    arrow
                    enterDelay={150}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.58rem",
                        color: "#5f7a86",
                        lineHeight: 1.2,
                        display: "block",
                        cursor: "help",
                      }}
                    >
                      {label}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 16,
              height: 2,
              background:
                "repeating-linear-gradient(90deg,#f83d3d 0 4px,transparent 4px 8px)",
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: "#5f7a86" }}>
            {baselineLabel} {formatNumber(Math.round(baseline), locale)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 16,
              height: 2,
              background:
                "repeating-linear-gradient(90deg,#5ed0fa 0 4px,transparent 4px 8px)",
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: "#5f7a86" }}>
            {averageLabel} {formatNumber(Math.round(average), locale)}
          </Typography>
        </Stack>
        {showWeekendLegend && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: "rgba(95, 122, 134, 0.16)",
                border: "1px solid rgba(95, 122, 134, 0.35)",
                borderRadius: 0.4,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" sx={{ color: "#5f7a86" }}>
              {weekendLabel}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
